import * as React from "react";
import { createContext } from "react";
import styled from "styled-components";
import {
  ConfirmImportRequest,
  ErrorResponse,
  Field,
  ImportItem,
  ImportStatusResponse,
  ItemConfirmOptions,
  NewFieldType,
} from "./models";
import FortnoxImportClient from "./import_client";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import Checkbox, { CheckboxStates } from "../common/Checkbox";
import { SessionStorage } from "../../persistence";
import moment, { Moment } from "moment";
import NewField from "./NewField";

export interface Props {
  singularName: string;
  pluralName: string;
  apiEndpoint: string;
}

const ImportPageWrapper = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  max-height: calc(100vh - 250px);
  gap: 0.5rem;

  & .content {
    overflow-y: auto;
  }
`;

const UnstyledList = styled.ul`
  list-style-type: none;
`;

const ErrorMessage = styled.span`
  color: red;
  font-weight: bold;
`;

const ItemWrapper = styled.div`
  max-width: 1000px;
  margin-bottom: 0.5rem;

  &.loading {
    opacity: 0.5;
  }

  &.has-errors {
    &,
    & table tr {
      background-color: rgba(255, 0, 0, 0.075);
    }
  }

  & > h3 {
    margin: 0;
  }

  & table {
    & th:nth-child(1) {
      width: 150px;
    }

    // Right align text in the first column
    & > * > tr > * {
      &:nth-child(1) {
        text-align: right;
        font-weight: bold;
        white-space: pre-wrap;
      }
    }

    & .overwrite {
      text-align: right;
      padding-right: 10px;
    }
  }
`;

const ValueWrapper = styled.span`
  &.old-value {
    color: #950101;
    text-decoration: line-through;
  }

  &.new-value {
    color: #019501;
  }

  & pre {
    display: inline;
  }
`;

const ButtonsWrapper = styled.div`
  width: 1000px;
  display: flex;
  flex-direction: row;
  gap: 5px;

  & button {
    margin-bottom: 0;
  }
`;

const QueryStatsWrapper = styled.sub``;

const ErrorWrapper = styled.span`
  display: block;
  color: red;

  & ul {
    list-style: none;
    margin: 0;
  }
`;

const ImportPage = ({ singularName, pluralName, apiEndpoint }: Props) => {
  let [status, setStatus] = React.useState<ImportStatusResponse | null>(null);
  let [loading, setLoading] = React.useState(true);
  let [error, setError] = React.useState<string | null>(null);
  let apiClient = React.useRef<FortnoxImportClient>();
  let isFirstRefresh = React.useRef(true);
  let keepAliveRef = React.useRef<number | null>(null);
  let [overwriteOptions, setOverwriteOptions] = React.useState<
    Record<string, string[]>
  >({});
  let [newFieldValues, setNewFieldValues] = React.useState<
    Record<string, Record<string, unknown | null>>
  >({});
  let queryStatusTimeoutId = React.useRef<number | null>(null);
  let cancelled = React.useRef(false);

  let hasChangedFields = React.useMemo<boolean>(() => {
    if (status == null || status.isRunning) {
      return false;
    }

    for (let i = 0; i < status.items.length; ++i) {
      let importItem = status.items[i];

      if (importItem.changedFields.length > 0) {
        return true;
      }
    }

    return false;
  }, [status]);

  const initialLoading = React.useMemo(() => {
    return loading && status == null;
  }, [loading, status]);

  let queryDates = React.useMemo<[Moment | null, Moment | null]>(() => {
    if (status == null) {
      return [null, null];
    }

    let queryStartDate = moment(status.queryDataStartDateTime);
    let queryEndDate: Moment | null = null;

    if (status.queryDataFinishDateTime != null) {
      queryEndDate = moment(status.queryDataFinishDateTime);
    }

    return [queryStartDate, queryEndDate];
  }, [status]);

  // Initialize stuff on component mount
  React.useEffect(() => {
    apiClient.current = new FortnoxImportClient(apiEndpoint);

    (async () => {
      const [data, response] = await apiClient.current!.newImport();
      handleStatusUpdate(data, response);

      if (data != null && !("error" in data)) {
        // Restore overwrite state if any
        const sessionOverwriteOptions = SessionStorage.deserialize<
          Record<string, string[]>
        >(`overwrite_state_${data.importId}`);

        if (sessionOverwriteOptions != null) {
          setOverwriteOptions(sessionOverwriteOptions);
        }

        // Restore new field values if any
        const sessionNewFieldValues = SessionStorage.deserialize<
          Record<string, Record<string, unknown>>
        >(`new_field_values_${data.importId}`);

        if (sessionNewFieldValues != null) {
          setNewFieldValues(sessionNewFieldValues);
        }
      }
    })();
  }, []);

  // Persist import settings to session
  React.useEffect(() => {
    if (status == null) {
      return;
    }

    SessionStorage.set(`overwrite_state_${status.importId}`, overwriteOptions);
    SessionStorage.set(`new_field_values_${status.importId}`, newFieldValues);
  }, [overwriteOptions, newFieldValues]);

  // Keep api client's endpoint up to date
  React.useEffect(() => {
    if (apiClient.current != null) {
      apiClient.current.apiEndpoint = apiEndpoint;
    }
  }, [apiEndpoint]);

  // Keep querying for status while the job is running
  React.useEffect(() => {
    if (status != null && status.isRunning) {
      (async () => {
        const sleepTime = isFirstRefresh.current ? 1000 : 3000;
        isFirstRefresh.current = false;

        await new Promise(
          (resolve) =>
            (queryStatusTimeoutId.current = setTimeout(resolve, sleepTime)),
        );

        if (queryStatusTimeoutId.current != null) {
          queryStatusTimeoutId.current = null;
        }

        const [data, response] = await apiClient.current!.status(
          status.importId,
        );

        if (cancelled.current) {
          return;
        }

        handleStatusUpdate(data, response);
      })();

      return () => {
        if (queryStatusTimeoutId.current != null) {
          clearTimeout(queryStatusTimeoutId.current);
          queryStatusTimeoutId.current = null;
        }
      };
    }
  }, [status]);

  // Keep site alive after query import is done
  React.useEffect(() => {
    if (status == null || status.isRunning) return;

    if (keepAliveRef.current != null) clearInterval(keepAliveRef.current);

    // Send keep alive once every minute
    keepAliveRef.current = setInterval(async () => {
      await apiClient.current?.keepAlive();
    }, 60_000);

    // Clear interval when component is unmounted
    return () => {
      if (keepAliveRef.current != null) {
        clearInterval(keepAliveRef.current);
      }
    };
  }, [status]);

  function handleStatusUpdate(
    data: ImportStatusResponse | ErrorResponse,
    response: Response,
  ) {
    setLoading(false);

    if ("error" in data) {
      setError(data.error);
    } else {
      setError(null);
      setStatus(data);

      data.items.sort((a, b) => {
        const isNewOrder = (b.isNew ? 1 : -1) - (a.isNew ? 1 : -1);

        if (isNewOrder != 0) {
          return isNewOrder;
        }

        const isLoadingOrder = (a.isLoading ? 1 : -1) - (b.isLoading ? 1 : -1);

        if (isLoadingOrder != 0) {
          return isLoadingOrder;
        }

        return a.id.localeCompare(b.id);
      });

      let newOverwriteOptions = { ...overwriteOptions };
      let newNewFieldValues = { ...newFieldValues };

      data.items.forEach((importItem) => {
        if (importItem.isLoading) {
          return;
        }

        const itemOverwriteOptions = newOverwriteOptions[importItem.id];

        if (itemOverwriteOptions != null) {
          // Remove from overwrite options fields that are no longer marked as changed
          for (let i = itemOverwriteOptions.length - 1; i >= 0; --i) {
            const fieldId = itemOverwriteOptions[i];
            let index = importItem.changedFields.indexOf(fieldId);

            if (index == -1) {
              itemOverwriteOptions.splice(i, 1);
            }
          }
        } else {
          newOverwriteOptions[importItem.id] = [...importItem.changedFields];
        }

        let newFieldValues = newNewFieldValues[importItem.id];

        if (newFieldValues == null) {
          newFieldValues = {};
          newNewFieldValues[importItem.id] = newFieldValues;
        }

        for (let i = 0; i < importItem.newFields.length; ++i) {
          const newField = importItem.newFields[i];

          if (newFieldValues[newField.id] === undefined) {
            newFieldValues[newField.id] = newField.defaultValue ?? null;
          }
        }
      });

      setOverwriteOptions(newOverwriteOptions);
      setNewFieldValues(newNewFieldValues);
    }
  }

  async function onClickCancel() {
    if (status == null || apiClient.current == null) {
      return;
    }

    setLoading(true);
    const [error, response] = await apiClient.current!.cancelImport(
      status.importId,
    );

    if (response.status != 200 && response.status != 404) {
      alert(
        `Misslyckades med att avbryta importen: ${error?.error ?? "(okänd orsak)"}`,
      );
    }

    if (queryStatusTimeoutId.current != null) {
      clearTimeout(queryStatusTimeoutId.current);
      queryStatusTimeoutId.current = null;
    }

    setLoading(false);
    setStatus(null);
    setError("Importen avbröts. Ladda om sidan om du vill starta en ny.");
    setOverwriteOptions({});
    setNewFieldValues({});
  }

  async function onClickImportAll() {
    if (status == null || apiClient.current == null) {
      return;
    }

    setLoading(true);

    const items: Record<string, ItemConfirmOptions> = {};

    for (let i = 0; i < status.items.length; ++i) {
      const item = status.items[i];
      items[item.id] = getImportOptions(item, overwriteOptions, newFieldValues);
    }

    const data: ConfirmImportRequest = {
      items,
    };

    const [responseData, response] = await apiClient.current.confirmImport(
      status.importId,
      data,
    );
    handleStatusUpdate(responseData, response);
  }

  function setOverrideOptionsForItem(item: ImportItem, newOptions: string[]) {
    const newValue = { ...overwriteOptions };
    newValue[item.id] = newOptions;
    setOverwriteOptions(newValue);
  }

  function setNewFieldValue(
    importItem: ImportItem,
    id: string,
    value: unknown | null,
  ) {
    const newValues = { ...newFieldValues };
    let valuesForItem = newValues[importItem.id];

    if (valuesForItem == null) {
      valuesForItem = {};
      newValues[importItem.id] = valuesForItem;
    }

    valuesForItem[id] = value;

    setNewFieldValues(newValues);
  }

  return (
    <ImportPageWrapper>
      <div className="instructions">
        <h3>Instruktioner</h3>
        <h4>Ikoner</h4>
        <UnstyledList>
          <li>
            <i className="fi-plus icon" style={{ color: "#019501" }}></i> =
            Skapa ny/nytt {singularName} i Kalle med uppgifter från Fortnox.
          </li>
          <li>
            <i className="fi-pencil icon" style={{ color: "#b7b700" }}></i> =
            Uppdatera existerande {singularName} i Kalle med uppgifter från
            Fortnox.
          </li>
        </UnstyledList>
        <h4>Värden</h4>
        <ul>
          <li>
            Värden som kommer ersättas är{" "}
            <ValueWrapper className="old-value">
              överstruken med röd text
            </ValueWrapper>{" "}
            medan det nya värdet är{" "}
            <ValueWrapper className="new-value">grön text</ValueWrapper>.
          </li>
        </ul>
      </div>
      <div className="content">
        {initialLoading && <span>Laddar...</span>}
        {status && (
          <>
            {status.items.map((item) => (
              <Item
                key={item.id}
                item={item}
                overwriteOptions={overwriteOptions[item.id] ?? []}
                setOverwriteOptions={(newOptions) =>
                  setOverrideOptionsForItem(item, newOptions)
                }
                newFieldData={status.newFieldTypeData}
                newFieldValues={newFieldValues[item.id]}
                setNewFieldValue={(id, value) =>
                  setNewFieldValue(item, id, value)
                }
              />
            ))}
          </>
        )}
        {!initialLoading &&
          status != null &&
          !status.isRunning &&
          !hasChangedFields && (
            <span>Alla {pluralName} i Kalle är redan uppdaterade.</span>
          )}
      </div>
      <div className="status-and-buttons">
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {!initialLoading && (
          <ButtonsWrapper>
            <button
              className="button primary"
              disabled={
                status?.hasQueryErrors ||
                status?.isRunning ||
                !hasChangedFields ||
                loading
              }
              onClick={onClickImportAll}
            >
              Importera alla
            </button>
            <button
              className="button warning"
              onClick={onClickCancel}
              disabled={status == null || !status.isRunning}
            >
              Avbryt
            </button>
          </ButtonsWrapper>
        )}
        {status && (
          <QueryStatsWrapper>
            {status?.isRunning && (
              <>
                <FontAwesomeIcon icon={faSpinner} spin={true} />{" "}
              </>
            )}
            Inläsning av data från Fortnox påbörjades{" "}
            {queryDates[0]?.calendar()}
            {!queryDates[1] && (
              <>
                {" ("}
                {queryDates[0]?.fromNow()}
                {")..."}
              </>
            )}
            {queryDates[1] && <> och slutfördes {queryDates[1].calendar()}</>}
          </QueryStatsWrapper>
        )}
      </div>
    </ImportPageWrapper>
  );
};

interface ItemProps {
  item: ImportItem;
  overwriteOptions: string[];
  setOverwriteOptions: (newOptions: string[]) => void;
  newFieldData: Record<NewFieldType, unknown>;
  newFieldValues: Record<string, unknown>;
  setNewFieldValue: (id: string, newValue: unknown) => void;
}

const Item = ({
  item,
  overwriteOptions,
  setOverwriteOptions,
  newFieldData,
  newFieldValues,
  setNewFieldValue,
}: ItemProps) => {
  if (!item.isLoading && item.changedFields.length == 0) {
    return null;
  }

  const hasFormErrors = React.useMemo(() => {
    return Object.keys(item.formErrors).length > 0;
  }, [item]);

  /**
   * Returns true if all fields with changed values are also added to overwriteOptions
   */
  function getAllFieldsOverwrittenState(): CheckboxStates {
    let allFound = true;

    for (const i in item.changedFields) {
      let id = item.changedFields[i];

      if (!overwriteOptions.includes(id)) {
        allFound = false;
        break;
      }
    }

    if (allFound) {
      return CheckboxStates.Checked;
    }

    if (overwriteOptions.length == 0) {
      return CheckboxStates.Unchecked;
    }

    return CheckboxStates.Indeterminate;
  }

  function isOverwritten(field: Field): boolean {
    return overwriteOptions.includes(field.id);
  }

  function onSetAllFieldsOverwritten(ev: React.ChangeEvent<HTMLInputElement>) {
    if (ev.target.checked) {
      setOverwriteOptions(item.changedFields);
    } else {
      setOverwriteOptions([]);
    }
  }

  function onChangeOverwriteOption(
    field: Field,
    ev: React.ChangeEvent<HTMLInputElement>,
  ) {
    if (ev.target.checked) {
      setOverwriteOptions([...overwriteOptions, field.id]);
    } else {
      setOverwriteOptions(overwriteOptions.filter((id) => id != field.id));
    }
  }

  return (
    <ItemWrapper
      className={classNames(
        item.isLoading && "loading",
        item.hasErrors && "has-errors",
      )}
    >
      <h3>
        {item.isLoading && (
          <>
            <FontAwesomeIcon icon={faSpinner} spin={true} />{" "}
          </>
        )}
        {item.isNew ? (
          <i className="fi-plus icon" style={{ color: "#019501" }}></i>
        ) : (
          <i className="fi-pencil icon" style={{ color: "#b7b700" }}></i>
        )}{" "}
        {item.id}
        {item.name != null && (
          <>
            {" - "}
            {item.name}
          </>
        )}
      </h3>
      <table className="table-small dataTable dtr-inline">
        <thead>
          <tr>
            <th>Fält</th>
            <th>Värde</th>
            {hasFormErrors && <th>Fel</th>}
            <th className="overwrite">
              Skriv över{" "}
              <Checkbox
                state={getAllFieldsOverwrittenState()}
                onChange={onSetAllFieldsOverwritten}
                readOnly={item.isNew}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {item.fields
            .filter((field) => item.changedFields.includes(field.id))
            .map((field) => (
              <tr key={field.id}>
                <td>{field.name}</td>
                <td>
                  <ValueDiff
                    oldValue={field.localValue}
                    newValue={field.remoteValue}
                  />
                </td>
                {hasFormErrors && (
                  <td>
                    <ErrorWrapper>
                      <ul>
                        {item.formErrors[field.id] &&
                          item.formErrors[field.id].map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        {item.importErrors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </ErrorWrapper>
                  </td>
                )}
                <td className="overwrite">
                  <Checkbox
                    state={
                      isOverwritten(field)
                        ? CheckboxStates.Checked
                        : CheckboxStates.Unchecked
                    }
                    onChange={(ev) => onChangeOverwriteOption(field, ev)}
                    readOnly={item.isNew}
                  />
                </td>
              </tr>
            ))}
          {!item.isLoading &&
            item.newFields.map((field) => (
              <tr key={field.id}>
                <td>{field.name}</td>
                <td>
                  <NewField
                    type={field.type}
                    defaultValue={field.defaultValue}
                    value={newFieldValues[field.id]}
                    typeData={newFieldData[field.type]}
                    onChange={(newValue) =>
                      setNewFieldValue(field.id, newValue)
                    }
                  />
                </td>
                {hasFormErrors && (
                  <td colSpan={2}>
                    <ErrorWrapper>
                      <ul>
                        {item.formErrors[field.id].map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </ErrorWrapper>
                  </td>
                )}
              </tr>
            ))}
        </tbody>
      </table>

      {(item.queryErrors.length > 0 || item.importErrors.length > 0) && (
        <>
          <h4>Importfel</h4>
          <ErrorWrapper>
            <ul>
              {item.queryErrors
                .concat(item.importErrors)
                .map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
            </ul>
          </ErrorWrapper>
        </>
      )}
    </ItemWrapper>
  );
};

interface ValueProps {
  oldValue: unknown;
  newValue: unknown;
}

const ValueDiff = ({ oldValue, newValue }: ValueProps) => {
  function renderValue(value: unknown): string | number | null {
    if (typeof value == "string") {
      return value;
    } else if (typeof value == "boolean") {
      return value ? "Ja" : "Nej";
    } else if (typeof value == "number") {
      return value;
    } else if (typeof value == "object") {
      if (value == null) {
        return null;
      }

      if ("name" in value && typeof value.name == "string") {
        return value.name;
      }
    }

    console.warn("Unknown value format. value =", value);
    return JSON.stringify(value);
  }

  const valuesMatch = oldValue == newValue;
  const renderedOldValue = renderValue(oldValue);
  const renderedNewValue = renderValue(newValue);
  let containsNewLine;

  if (typeof renderedOldValue == "string") {
    if (renderedOldValue.includes("\n")) {
      containsNewLine = true;
    }
  }

  if (typeof renderedNewValue == "string") {
    if (renderedNewValue.includes("\n")) {
      containsNewLine = true;
    }
  }

  return (
    <div>
      {valuesMatch && <ValueWrapper>{renderedNewValue}</ValueWrapper>}
      {!valuesMatch && (
        <>
          {oldValue != null && oldValue != "" && (
            <>
              <ValueWrapper className="old-value">
                <pre>{renderedOldValue}</pre>
              </ValueWrapper>
              {containsNewLine && <br />}
              <span> → </span>
              {containsNewLine && <br />}
            </>
          )}
          <ValueWrapper className={classNames(!valuesMatch && "new-value")}>
            <pre>{renderedNewValue}</pre>
          </ValueWrapper>
        </>
      )}
    </div>
  );
};

function getImportOptions(
  item: ImportItem,
  overwriteOptions: Record<string, string[]>,
  newFieldValues: Record<string, Record<string, unknown | null>>,
): ItemConfirmOptions {
  const overwriteFields = overwriteOptions[item.id] ?? {};
  const newFields = newFieldValues[item.id];

  return {
    overwriteFields,
    newFields,
  };
}

export default ImportPage;
